import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  Copy,
  CreditCard,
  LifeBuoy,
  Loader2,
  LogOut,
  Mail,
  Power,
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import QRCode from "qrcode";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

type Session = {
  user: { id: string; email: string } | null;
  csrfToken: string | null;
};

type Computer = {
  id: string;
  status: string;
  error?: string;
  created_at: string;
  updated_at: string;
  last_seen_at: string | null;
  last_error_code: string | null;

  // Fly machine metadata
  fly_app_name: string | null;
  machine_id: string | null;
  machine_name: string | null;
  display_name: string | null;
  volume_id: string | null;
  region: string | null;
  image_ref: string | null;
  provisioning_status: string | null;
  resource_status: string | null;
  resource_error_code: string | null;
  observed_cpu_kind: string | null;
  observed_cpus: number | null;
  observed_memory_mb: number | null;
  observed_volume_size_gb: number | null;
  active_operation: string | null;
  operation_lease_expires_at: string | null;
  desired_resources?: {
    cpuKind: string;
    cpus: number;
    memoryMb: number;
    minimumVolumeSizeGb: number;
  };

  // Codex auth summaries returned by the computer endpoint.
  codex_auth_status: string | null;
  codex_auth_method: string | null;
  codex_auth_email: string | null;
  codex_logged_in_at: string | null;
};

type DoctorReport = {
  overallStatus?: string;
  codex_version?: string;
};

type CodexInfo = {
  installed: boolean;
  version?: string;
  doctor?: DoctorReport;
  error?: string;
};

type CodexAuth = {
  status: "idle" | "pending" | "logged_in" | "error";
  method?: string;
  email?: string;
  verificationUrl?: string;
  userCode?: string;
  error?: string;
  startedAt?: number;
};

type RemotePairing = {
  ok: boolean;
  pairingCode?: string;
  manualPairingCode?: string;
  environmentId?: string;
  expiresAt?: number;
  error?: string;
};

function friendlyComputerStatus(status: string): {
  title: string;
  description: string;
  tone: "ok" | "busy" | "bad" | "idle";
} {
  switch (status) {
    case "online":
      return {
        title: "On and running",
        description: "Your computer is awake and ready.",
        tone: "ok",
      };
    case "provisioning":
      return {
        title: "Setting up your computer…",
        description: "This usually takes a minute or two. No need to refresh.",
        tone: "busy",
      };
    case "resizing":
    case "pending":
    case "retry":
      return {
        title: "Updating your computer…",
        description: "Your files are safe while Flock applies the latest resources.",
        tone: "busy",
      };
    case "resetting":
      return {
        title: "Starting fresh…",
        description: "We're giving you a clean computer. Hang tight.",
        tone: "busy",
      };
    case "error":
      return {
        title: "Something needs attention",
        description: "Your computer hit a snag. A restart usually fixes it.",
        tone: "bad",
      };
    case "unprovisioned":
      return {
        title: "Ready to create",
        description: "Choose a name for your Codex computer.",
        tone: "idle",
      };
    default:
      return { title: "Working on it…", description: "Give us a moment.", tone: "busy" };
  }
}

function statusDotClasses(tone: "ok" | "busy" | "bad" | "idle") {
  if (tone === "ok") return "bg-emerald-500";
  if (tone === "bad") return "bg-red-500";
  if (tone === "busy") return "bg-amber-500 animate-pulse";
  return "bg-zinc-400";
}

function StepBadge({ number, done }: { number: number; done: boolean }) {
  return (
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
        done ? "bg-emerald-500 text-white" : "bg-zinc-900 text-zinc-50"
      }`}
    >
      {done ? <Check size={18} /> : number}
    </div>
  );
}

export function Dashboard() {
  const [session, setSession] = useState<Session | null>(null);
  const [computer, setComputer] = useState<Computer | null>(null);
  const [computerLoaded, setComputerLoaded] = useState(false);
  const [codex, setCodex] = useState<CodexInfo | null>(null);
  const [auth, setAuth] = useState<CodexAuth | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [remotePairing, setRemotePairing] = useState<RemotePairing | null>(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteEnabled, setRemoteEnabled] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<"restart" | "reset" | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [computerName, setComputerName] = useState("remote-codex");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const r = await fetch("/api/session");
      if (r.status === 401) {
        window.location.href = "/login";
        return;
      }
      const s = (await r.json()) as Session;
      setSession(s);
    })();
  }, []);

  useEffect(() => {
    if (!session) return;

    async function loadComputer() {
      const r = await fetch("/api/computer");
      if (r.ok) {
        const data = (await r.json()) as { computer: Computer | null };
        setComputer(data.computer);
      }
      setComputerLoaded(true);
    }

    void loadComputer();

    const computerInterval = setInterval(() => void loadComputer(), 5000);
    return () => {
      clearInterval(computerInterval);
    };
  }, [session]);

  useEffect(() => {
    if (!session || !computer?.machine_id || computer.status !== "online") return;

    async function loadCodex() {
      const r = await fetch("/api/codex");
      if (r.ok) {
        const data = (await r.json()) as { codex: CodexInfo };
        setCodex(data.codex);
      }
    }

    async function loadAuth() {
      const r = await fetch("/api/codex/auth");
      if (r.ok) {
        const data = (await r.json()) as { auth: CodexAuth };
        setAuth(data.auth);
      }
    }

    async function loadPairing() {
      const r = await fetch("/api/codex/pair/status");
      if (r.ok) {
        const data = (await r.json()) as { pair: RemotePairing };
        setRemotePairing(data.pair);
        setRemoteEnabled(data.pair.ok);
      }
    }

    void loadCodex();
    void loadAuth();
    void loadPairing();

    const codexInterval = setInterval(() => void loadCodex(), 30000);
    const authInterval = setInterval(() => void loadAuth(), 5000);
    const remoteInterval = setInterval(() => void loadPairing(), 10000);
    return () => {
      clearInterval(codexInterval);
      clearInterval(authInterval);
      clearInterval(remoteInterval);
    };
  }, [session, computer?.machine_id, computer?.status]);

  async function logout() {
    if (!session?.csrfToken) return;
    await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    window.location.href = "/login";
  }

  async function createComputer(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session?.csrfToken || createLoading) return;

    setCreateLoading(true);
    setCreateError(null);
    const r = await fetch("/api/computer", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": session.csrfToken,
      },
      body: JSON.stringify({ name: computerName }),
    });
    const data = (await r.json()) as {
      computer?: Computer;
      error?: string | { issues?: Array<{ message?: string }> };
    };

    if (r.ok && data.computer) {
      setComputer(data.computer);
    } else {
      const validationMessage =
        typeof data.error === "object" ? data.error.issues?.[0]?.message : undefined;
      const message =
        typeof data.error === "string" && data.error === "computer_creation_disabled"
          ? "New computer setup is temporarily paused. Try again shortly."
          : undefined;
      setCreateError(
        validationMessage ?? message ?? "We couldn't create your computer. Try another name."
      );
    }
    setCreateLoading(false);
  }

  async function restartComputer() {
    if (!session?.csrfToken) return;
    setActionLoading("restart");
    const r = await fetch("/api/computer/restart", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    if (r.ok) {
      const data = (await r.json()) as { computer: Computer };
      setComputer(data.computer);
    }
    setActionLoading(null);
  }

  async function resetWorkspace() {
    if (!session?.csrfToken) return;
    if (
      !window.confirm(
        "Start fresh? This erases all files and settings on your computer and gives you a clean one. This can't be undone."
      )
    ) {
      return;
    }
    setActionLoading("reset");
    const r = await fetch("/api/computer/reset", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    if (r.ok) {
      const data = (await r.json()) as { computer: Computer };
      setComputer(data.computer);
    }
    setActionLoading(null);
  }

  async function startLogin() {
    if (!session?.csrfToken) return;
    setLoginLoading(true);
    const r = await fetch("/api/codex/login/start", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    const data = (await r.json()) as {
      login?: CodexAuth & { ok: boolean };
      error?: string;
      message?: string;
    };
    setLoginLoading(false);
    if (data.login) {
      setAuth(data.login);
    } else if (!r.ok) {
      setAuth({ status: "error", error: data.message ?? data.error ?? "Login failed" });
    }
  }

  async function cancelLogin() {
    if (!session?.csrfToken) return;
    await fetch("/api/codex/login/cancel", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    const r = await fetch("/api/codex/auth");
    if (r.ok) setAuth(((await r.json()) as { auth: CodexAuth }).auth);
  }

  async function disconnectCodex() {
    if (!session?.csrfToken) return;
    await fetch("/api/codex/logout", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    const r = await fetch("/api/codex/auth");
    if (r.ok) setAuth(((await r.json()) as { auth: CodexAuth }).auth);
  }

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // ignore
    }
  }

  async function startRemoteDaemon() {
    if (!session?.csrfToken) throw new Error("Session not loaded");
    const r = await fetch("/api/codex/remote/start", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    const data = (await r.json()) as {
      remote?: { ok: boolean; environmentId?: string; error?: string };
      error?: string;
      message?: string;
    };
    if (!r.ok || !data.remote?.ok) {
      throw new Error(
        data.remote?.error ?? data.message ?? data.error ?? "Failed to start Remote daemon"
      );
    }
    return data.remote;
  }

  async function fetchPairingCode() {
    if (!session?.csrfToken) throw new Error("Session not loaded");
    const r = await fetch("/api/codex/pair", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    const data = (await r.json()) as { pair?: RemotePairing; error?: string; message?: string };
    if (!r.ok || !data.pair) {
      throw new Error(data.message ?? data.error ?? "Failed to generate pairing code");
    }
    return data.pair;
  }

  async function enableRemotePairing() {
    if (!session?.csrfToken) return;
    setRemoteLoading(true);
    try {
      await startRemoteDaemon();
      const pair = await fetchPairingCode();
      setRemotePairing(pair);
      setRemoteEnabled(pair.ok);
    } catch (err) {
      setRemotePairing({
        ok: false,
        error: err instanceof Error ? err.message : "Failed to enable Remote",
      });
      setRemoteEnabled(false);
    } finally {
      setRemoteLoading(false);
    }
  }

  async function regenerateRemotePairing() {
    if (!session?.csrfToken) return;
    setRemoteLoading(true);
    try {
      // The daemon may have stopped; ensure it is running before minting a new code.
      await startRemoteDaemon();
      const pair = await fetchPairingCode();
      setRemotePairing(pair);
      setRemoteEnabled(pair.ok);
    } catch (err) {
      setRemotePairing({
        ok: false,
        error: err instanceof Error ? err.message : "Failed to regenerate pair code",
      });
      setRemoteEnabled(false);
    } finally {
      setRemoteLoading(false);
    }
  }

  async function stopRemotePairing() {
    if (!session?.csrfToken) return;
    setRemoteLoading(true);
    await fetch("/api/codex/remote/stop", {
      method: "POST",
      headers: { "X-CSRF-Token": session.csrfToken },
    });
    setRemotePairing(null);
    setRemoteEnabled(false);
    setQrDataUrl(null);
    setRemoteLoading(false);
  }

  useEffect(() => {
    if (!remotePairing?.pairingCode) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    const pairingUrl = new URL("https://chatgpt.com/codex/pair");
    pairingUrl.searchParams.set("pairing_code", remotePairing.pairingCode);
    QRCode.toDataURL(pairingUrl.toString(), {
      width: 200,
      margin: 2,
      color: { dark: "#18181b", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [remotePairing?.pairingCode]);

  const authExpiresAt = auth?.startedAt ? new Date(auth.startedAt + 15 * 60 * 1000) : null;
  const authExpiryText = authExpiresAt
    ? `This code works until ${authExpiresAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.`
    : "";

  const pairExpiresAt = remotePairing?.expiresAt ? new Date(remotePairing.expiresAt * 1000) : null;
  const pairExpiryText = pairExpiresAt
    ? `This code works until ${pairExpiresAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.`
    : "";

  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 text-zinc-900">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 size={16} className="animate-spin" />
          Loading your computer…
        </div>
      </div>
    );
  }

  const resourceBusy = Boolean(
    computer &&
    (["pending", "provisioning", "resizing", "retry"].includes(computer.resource_status ?? "") ||
      computer.active_operation)
  );
  const canManageMachine = Boolean(computer?.machine_id && !resourceBusy);
  const computerReady = computer?.status === "online" && !resourceBusy;
  const codexConnected = auth?.status === "logged_in";
  const phonePaired = remoteEnabled && remotePairing?.ok === true;
  const showSetupPrerequisites = !computerReady || !codexConnected;
  const pairingNotStarted = remotePairing?.error === "no_active_pairing";
  const displayStatus = resourceBusy
    ? computer?.resource_status === "provisioning"
      ? "provisioning"
      : "resizing"
    : (computer?.status ?? "unprovisioned");
  const status = friendlyComputerStatus(displayStatus);
  const supportHref = `mailto:hello@flock.build?subject=${encodeURIComponent(
    "Flock support request"
  )}&body=${encodeURIComponent(
    `Account email: ${session.user?.email ?? ""}\n\nTell us how we can help:\n`
  )}`;

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900">
      <header className="sticky top-0 z-50 h-16 w-full border-b border-zinc-200/70 bg-zinc-50/80 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link
            to="/"
            className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60"
          >
            <Logo showWordmark />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-zinc-500 sm:inline">
              {session.user?.email ?? ""}
            </span>
            <Link
              to="/billing"
              className="inline-flex items-center text-sm font-medium text-zinc-500 hover:text-zinc-900"
            >
              <CreditCard size={14} className="mr-1.5" />
              Billing
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-zinc-500 hover:text-zinc-900"
            >
              <LogOut size={14} className="mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <aside className="mb-5 overflow-hidden rounded-2xl bg-zinc-900 text-white shadow-lg shadow-zinc-950/10">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-amber-200">
                <LifeBuoy size={19} />
              </span>
              <div>
                <p className="text-sm font-semibold">Need a hand?</p>
                <p className="mt-1 max-w-lg text-sm leading-relaxed text-zinc-300">
                  Send us a note about setup, billing, or anything that isn't working as expected.
                  You will get a response within a couple of hours.
                </p>
              </div>
            </div>
            <a
              href={supportHref}
              className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-white px-3 text-sm font-medium text-zinc-900 transition-colors outline-none hover:bg-amber-50 focus-visible:ring-3 focus-visible:ring-amber-200/40 active:translate-y-px"
            >
              <Mail size={15} />
              Email support
            </a>
          </div>
        </aside>

        <h1 className="mt-8 text-2xl font-semibold tracking-tight">
          {codexConnected && phonePaired ? "You're all set" : "Set up your Codex"}
        </h1>
        <p className="mt-1 text-zinc-500">
          {codexConnected && phonePaired
            ? "Codex is connected and ready on your phone. It stays on even when you're away."
            : "Three quick steps and Codex is yours, anywhere."}
        </p>

        <div className="mt-6 space-y-4">
          {/* Step 1: Computer */}
          {showSetupPrerequisites && (
            <Card className="border-zinc-200 bg-white shadow-sm">
              <CardContent className="flex gap-4 py-5">
                <StepBadge number={1} done={computerReady} />
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-semibold tracking-tight">Your computer</h2>
                  {computerReady ? (
                    <p className="mt-1 text-sm text-zinc-500">
                      {computer?.display_name ?? "remote-codex"} is ready to go. It stays on around
                      the clock.
                    </p>
                  ) : computerLoaded && !computer ? (
                    <form onSubmit={createComputer} className="mt-3 space-y-3">
                      <div className="max-w-sm">
                        <label
                          htmlFor="computer-name"
                          className="text-sm font-medium text-zinc-700"
                        >
                          Computer name
                        </label>
                        <Input
                          id="computer-name"
                          value={computerName}
                          onChange={(event) => setComputerName(event.target.value)}
                          maxLength={23}
                          pattern="[a-z0-9]+(-[a-z0-9]+)*"
                          autoComplete="off"
                          spellCheck={false}
                          disabled={createLoading}
                          className="mt-1 h-9 bg-white font-mono"
                        />
                        <p className="mt-1.5 text-xs text-zinc-400">
                          Lowercase letters, numbers, and hyphens.
                        </p>
                      </div>
                      {createError && <p className="text-sm text-red-600">{createError}</p>}
                      <Button
                        type="submit"
                        size="sm"
                        disabled={createLoading}
                        className="bg-zinc-900 text-zinc-50 hover:bg-zinc-800"
                      >
                        {createLoading ? (
                          <>
                            <Loader2 size={14} className="mr-1.5 animate-spin" />
                            Creating…
                          </>
                        ) : (
                          "Create computer"
                        )}
                      </Button>
                    </form>
                  ) : (
                    <div className="mt-1 flex items-center gap-2 text-sm text-zinc-500">
                      {status.tone === "busy" && <Loader2 size={14} className="animate-spin" />}
                      <span>{status.description}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Connect ChatGPT */}
          {showSetupPrerequisites && (
            <Card className="border-zinc-200 bg-white shadow-sm">
              <CardContent className="flex gap-4 py-5">
                <StepBadge number={2} done={codexConnected} />
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-semibold tracking-tight">
                    Connect your ChatGPT account
                  </h2>

                  {!auth || auth.status === "idle" ? (
                    <div className="mt-1 space-y-3">
                      <p className="text-sm text-zinc-500">
                        Link your ChatGPT account so Codex can work for you. You only do this once.
                      </p>
                      <Button
                        size="sm"
                        onClick={() => void startLogin()}
                        disabled={loginLoading || !computerReady}
                        className="bg-zinc-900 text-zinc-50 hover:bg-zinc-800"
                      >
                        {loginLoading ? "One moment…" : "Connect ChatGPT"}
                      </Button>
                      {!computerReady && (
                        <p className="text-xs text-zinc-400">
                          Available once your computer finishes starting up.
                        </p>
                      )}
                    </div>
                  ) : auth.status === "pending" && auth.verificationUrl && auth.userCode ? (
                    <div className="mt-2 space-y-3 rounded-xl bg-zinc-50 p-4">
                      <p className="text-sm text-zinc-700">
                        <span className="font-medium">Open this link</span> on any device and enter
                        the code below:
                      </p>
                      <a
                        href={auth.verificationUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block break-all text-sm font-medium text-zinc-900 underline underline-offset-4 hover:text-zinc-600"
                      >
                        {auth.verificationUrl}
                      </a>
                      <div className="flex items-center gap-2">
                        <code className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 font-mono text-lg tracking-widest">
                          {auth.userCode}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void copyCode(auth.userCode!)}
                          className="h-8 text-zinc-500 hover:text-zinc-900"
                        >
                          {copied === auth.userCode ? (
                            <>
                              <Check size={14} className="mr-1.5 text-emerald-600" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy size={14} className="mr-1.5" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                      {authExpiryText && <p className="text-xs text-zinc-400">{authExpiryText}</p>}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void cancelLogin()}
                        className="text-zinc-500 hover:text-zinc-900"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : auth.status === "logged_in" ? (
                    <div className="mt-1 space-y-2">
                      <p className="text-sm text-zinc-500">
                        Connected{auth.email ? ` as ${auth.email}` : ""}. You're done here.
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void disconnectCodex()}
                        className="h-7 px-2 text-xs text-zinc-400 hover:text-zinc-700"
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-1 space-y-3">
                      <p className="text-sm text-red-600">
                        {auth.error ?? "That didn't work. Give it another try."}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void startLogin()}
                        disabled={loginLoading}
                        className="border-zinc-300"
                      >
                        Try again
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Pair phone */}
          <Card className="border-zinc-200 bg-white shadow-sm">
            <CardContent className="flex gap-4 py-5">
              <StepBadge number={3} done={phonePaired} />
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold tracking-tight">Put Codex on your phone</h2>

                {!codexConnected ? (
                  <p className="mt-1 text-sm text-zinc-500">
                    Finish step 2 first, then we'll show you a QR code to scan.
                  </p>
                ) : remotePairing === null || remoteEnabled === false ? (
                  pairingNotStarted ? (
                    <Button
                      size="lg"
                      onClick={() => void enableRemotePairing()}
                      disabled={remoteLoading}
                      className="mt-4 h-11 w-full bg-zinc-900 text-zinc-50 hover:bg-zinc-800"
                    >
                      {remoteLoading ? "Creating pairing code…" : "Create pairing code"}
                    </Button>
                  ) : (
                    <div className="mt-1 space-y-3">
                      {remotePairing?.error ? (
                        <p className="text-sm text-red-600">{remotePairing.error}</p>
                      ) : (
                        <p className="text-sm text-zinc-500">
                          Get a QR code to scan with the ChatGPT app on your phone.
                        </p>
                      )}
                      <Button
                        size="sm"
                        onClick={() => void enableRemotePairing()}
                        disabled={remoteLoading}
                        className="bg-zinc-900 text-zinc-50 hover:bg-zinc-800"
                      >
                        {remoteLoading
                          ? "One moment…"
                          : remotePairing?.error
                            ? "Try again"
                            : "Show QR code"}
                      </Button>
                    </div>
                  )
                ) : (
                  <div className="mt-3 flex flex-col gap-5 sm:flex-row">
                    <div className="space-y-2">
                      {qrDataUrl ? (
                        <img
                          src={qrDataUrl}
                          alt="QR code to pair your phone"
                          className="rounded-xl border border-zinc-200 bg-white p-2"
                          width={180}
                          height={180}
                        />
                      ) : (
                        <div className="flex h-[180px] w-[180px] items-center justify-center rounded-xl border border-zinc-200 bg-zinc-50 text-zinc-400">
                          <Loader2 size={20} className="animate-spin" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-4">
                      <ol className="space-y-1.5 text-sm text-zinc-600">
                        <li>1. Scan this code with your phone's camera</li>
                        <li>2. Follow the link to pair your computer</li>
                      </ol>

                      {remotePairing.manualPairingCode && (
                        <div>
                          <p className="text-xs text-zinc-400">Can't scan? Type this instead:</p>
                          <div className="mt-1 flex items-center gap-2">
                            <code className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-1.5 font-mono text-base tracking-widest">
                              {remotePairing.manualPairingCode}
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void copyCode(remotePairing.manualPairingCode!)}
                              className="h-8 text-zinc-500 hover:text-zinc-900"
                            >
                              {copied === remotePairing.manualPairingCode ? (
                                <Check size={14} className="text-emerald-600" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </Button>
                          </div>
                        </div>
                      )}

                      {pairExpiryText && <p className="text-xs text-zinc-400">{pairExpiryText}</p>}

                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void regenerateRemotePairing()}
                          disabled={remoteLoading}
                          className="border-zinc-300"
                        >
                          <RefreshCw size={14} className="mr-1.5" />
                          New code
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void stopRemotePairing()}
                          disabled={remoteLoading}
                          className="text-zinc-500 hover:text-zinc-900"
                        >
                          Turn off pairing
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Manage */}
        <section className="mt-12">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">
            Manage your computer
          </h2>
          <Card className="mt-3 border-zinc-200 bg-white shadow-sm">
            <CardContent className="divide-y divide-zinc-100 py-0">
              <div className="flex items-center gap-3 py-4">
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusDotClasses(status.tone)}`}
                />
                <div>
                  <p className="text-sm font-medium">{status.title}</p>
                  <p className="text-sm text-zinc-500">{computer?.error ?? status.description}</p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 py-4">
                <div>
                  <p className="text-sm font-medium">Restart</p>
                  <p className="text-sm text-zinc-500">
                    Turns it off and on again. Fixes most hiccups — your files are safe.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void restartComputer()}
                  disabled={!canManageMachine || actionLoading !== null}
                  className="shrink-0 border-zinc-300"
                >
                  <Power size={14} className="mr-1.5" />
                  {actionLoading === "restart" ? "Restarting…" : "Restart"}
                </Button>
              </div>

              <div className="flex items-center justify-between gap-4 py-4">
                <div>
                  <p className="text-sm font-medium">Start fresh</p>
                  <p className="text-sm text-zinc-500">
                    Erases everything and gives you a clean computer. Can't be undone.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void resetWorkspace()}
                  disabled={!canManageMachine || actionLoading !== null}
                  className="shrink-0 border-zinc-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <RotateCcw size={14} className="mr-1.5" />
                  {actionLoading === "reset" ? "Resetting…" : "Start fresh"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Technical details, tucked away */}
          {computer && (
            <details className="mt-4 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
              <summary className="cursor-pointer select-none text-sm font-medium text-zinc-500 hover:text-zinc-900">
                Technical details
              </summary>
              <div className="mt-3 space-y-1 font-mono text-xs text-zinc-500">
                <p>ID: {computer.id}</p>
                {computer.display_name && <p>Name: {computer.display_name}</p>}
                <p>Status: {computer.status}</p>
                {computer.machine_id && (
                  <p>
                    Machine: {computer.machine_name ?? computer.machine_id}
                    {computer.region && ` (${computer.region})`}
                  </p>
                )}
                {computer.volume_id && <p>Volume: {computer.volume_id}</p>}
                {computer.provisioning_status && (
                  <p>Provisioning: {computer.provisioning_status}</p>
                )}
                {computer.resource_status && <p>Resources: {computer.resource_status}</p>}
                {computer.observed_cpus && computer.observed_memory_mb && (
                  <p>
                    Compute: {computer.observed_cpus} shared CPU
                    {computer.observed_cpus === 1 ? "" : "s"}, {computer.observed_memory_mb} MB RAM
                  </p>
                )}
                {computer.observed_volume_size_gb && (
                  <p>Storage: {computer.observed_volume_size_gb} GB</p>
                )}
                {computer.last_seen_at && (
                  <p>Last seen: {new Date(computer.last_seen_at).toLocaleString()}</p>
                )}
                <p>Created: {new Date(computer.created_at).toLocaleString()}</p>
                {codex && (
                  <>
                    <p>Codex installed: {codex.installed ? "yes" : "no"}</p>
                    {codex.version && <p>Codex version: {codex.version}</p>}
                    {codex.doctor?.overallStatus && <p>Doctor: {codex.doctor.overallStatus}</p>}
                    {codex.error && <p>Codex error: {codex.error}</p>}
                  </>
                )}
                {remotePairing?.environmentId && <p>Environment: {remotePairing.environmentId}</p>}
              </div>
            </details>
          )}
        </section>
      </main>
    </div>
  );
}
